/**
 * IS THE OPERATOR TYPING, AND CAN THE READER SEE THIS?
 *
 * Two questions every keyboard mechanism on this platform asks before it does
 * anything, and the answers are here so they cannot be answered two ways.
 *
 * A LETTER IS A LETTER INSIDE A BOX. The whole of the single-key layer rests on
 * being able to tell "n for new" from somebody typing a patient's name, and the
 * test is the element the keystroke landed on rather than a flag a screen sets:
 * a screen that forgets to set the flag while a dialogue is open types `n` into
 * nothing and presses a button instead.
 *
 * AND A CONTROL NOBODY CAN SEE IS NOT A CONTROL. A `/` must not jump into a
 * search box inside a folded rail, and the topmost overlay must be one that is
 * actually on screen: `display: none` and a zero-size box are both "not here".
 */

/** Elements that take text, and anything a page has marked editable. */
export function isTypingTarget(e: Pick<Event, "target">): boolean {
  const el = e.target;
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag !== "INPUT") return false;

  // A CHECKBOX IS NOT A TEXT BOX. Space toggles it and every other key is free,
  // which is what lets a letter still work while a tick has focus.
  const type = (el as HTMLInputElement).type;
  const pressed = new Set(["checkbox", "radio", "button", "submit", "reset", "file", "image"]);
  return !pressed.has(type);
}

/**
 * On screen at all: mounted, and not hidden by an attribute or by its style.
 *
 * DECLARED VISIBILITY, NOT MEASURED SIZE. A box with no layout is not evidence
 * of absence — jsdom measures every element at 0×0, so a rule written against
 * `getBoundingClientRect` is one no test can check, and an untestable rule is
 * one that quietly stops holding. What this is actually guarding against is a
 * control inside a folded rail or a closed dialogue, and those are `display:
 * none` or unmounted, which is exactly what is asked here.
 */
export function isVisible(el: Element | null | undefined): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (!el.isConnected) return false;

  // UP THE TREE, because `display` is not inherited: a field inside a folded
  // rail has an ordinary display of its own and is not on screen. The walk is
  // what a measured box would have caught for free, and what a check on the one
  // element silently misses.
  for (let node: HTMLElement | null = el; node; node = node.parentElement) {
    if (node.hidden || node.getAttribute("aria-hidden") === "true") return false;
    const style = getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") return false;
  }
  return true;
}
